import * as React from 'react';
import { type AxiosResponse } from 'axios';

import {
	type LoaderProviderData,
	useLoaderContext,
} from '~/providers/loader-context';
import {
	type ModalProviderData,
	useModalContext,
} from '~/providers/modal-context';
import {
	type ServicesProviderData,
	useServicesContext,
} from '~/providers/services-context';
import { $http, type IHttpOptions } from '~/services/util/$http';
import { removeLeadingSlash } from '~/utils/removeLeadingSlash';

import store from './betslip/betslip.store';
import { getSearchParamsAndHashFragment } from './betslip/util/utils';

interface WithContext {
	loaderContext: LoaderProviderData;
	modalContext: ModalProviderData;
	servicesContext: ServicesProviderData;
}

interface LinkProps extends WithContext {
	children?: any;
	childrenData?: any;
	className?: string;
	id?: string;
	legacyAjaxRequest?: boolean;
	marketAjaxUrl?: string;
	overrideFn?: (e: React.MouseEvent) => void;
	preCallback?: () => void;
	rel?: string;
	style?: React.CSSProperties;
	target?: string;
	title?: string;
	to?: string;
	url?: string;
}

class Link extends React.Component<LinkProps> {
	protected baseUrl: string;

	private updateClient: (key: string, val: any) => void;

	constructor(public props: LinkProps) {
		super(props);

		this.baseUrl = props.loaderContext.baseUrlWithProtocolAndRepub;

		this.generateBaseProps = this.generateBaseProps.bind(this);
		this.generateInternalUrl = this.generateInternalUrl.bind(this);
		this.generateUrl = this.generateUrl.bind(this);

		this.updateClient = store.getState().updateClient;
	}

	public shouldComponentUpdate(nextProps: LinkProps) {
		return (
			this.props.childrenData !== nextProps.childrenData ||
			this.props.to !== nextProps.to ||
			this.props.className !== nextProps.className ||
			nextProps.childrenData === 'force'
		);
	}

	protected handleClick = (e: React.MouseEvent, url: string): void => {
		const { modalContext, overrideFn, preCallback, servicesContext, target } =
			this.props;

		const { fullMarketAjaxUrl, isExternalUrl } = this.generateUrl();

		if ((e.metaKey || e.ctrlKey) && !isExternalUrl) return;

		e.preventDefault();

		if (!url) {
			return;
		}

		if (preCallback) {
			preCallback();
		}

		if (overrideFn) {
			overrideFn(e);
			return;
		}

		if (typeof target !== 'undefined' || isExternalUrl) {
			window.open(url, '_blank');
			return;
		}

		this.updateClient('showSpinner', true);

		const { hasSearchParams, searchParamsString } =
			getSearchParamsAndHashFragment(url);

		const requestURL =
			fullMarketAjaxUrl || hasSearchParams
				? `${fullMarketAjaxUrl || url}&ajax=1`
				: `${url}?ajax=1`;

		// Adding '?ajax=1' is a fix to stop ajax JSON pages caching in Chrome
		const options: IHttpOptions = {
			method: 'GET',
			url: requestURL,
		};

		// If you want to server render every page.
		// window.open(url, "_self");

		// If you want to ajax json for pages.

		$http(options)
			.then((res: AxiosResponse) => {
				let data = null;

				if (res.data?.data) {
					data = res.data.data;

					if (fullMarketAjaxUrl != null) {
						servicesContext.linkActions.dynamicUpdateMarketData(url);
					} else {
						this.updateClient('showSpinner', false);
						servicesContext.linkActions.newPage(url, data);
					}
				}
			})
			.catch(e => {
				// 500 errors go in here so we need to push the new url to history
				// before throwing the error so that our error handling does not
				// replace the previous pages state.
				// servicesContext.linkActions.newPage(url, {});

				const status = e?.response?.status;

				if (typeof status === 'number' && status >= 400 && status <= 499) {
					window.open(`${this.baseUrl}404.html`, '_self');
				} else if (
					typeof status === 'number' &&
					status >= 500 &&
					status <= 599
				) {
					window.open(`${this.baseUrl}500.html`, '_self');
				}

				this.updateClient('ajaxError', true);
				// throw new Error('Link: A bad response was received from the server whilst trying to navigate to a new url.');
			})
			.finally(() => {
				this.updateClient('showMenu', false);
				this.updateClient('showAllSports', false);
				this.updateClient('showSpinner', false);
				this.updateClient('showAllSportsBottom', false);
				this.updateClient('navDrawerOpen', false);
				this.updateClient('betslipOpen', false);

				// Update context searchString
				if (hasSearchParams) {
					const [_, dispatchFromContext] = modalContext;
					dispatchFromContext({
						type: 'SET_SEARCH_STRING',
						payload: { searchString: searchParamsString },
					});
				}
			});
	};

	/*
      Prepends http to any url if it doesn't have it
      Ideally all http servers would redirect to an https one
      meanwhile accessing an http site with https would show
      an exception page
    */
	protected formatExternalUrl(initialUrl: string): string {
		return initialUrl?.indexOf('://') === -1
			? `http://${initialUrl}`
			: initialUrl;
	}

	private generateBaseProps() {
		const {
			childrenData: _childrenData,
			legacyAjaxRequest: _legacyAjaxRequest,
			loaderContext: _loaderContext,
			modalContext: _modalContext,
			overrideFn: _overrideFn,
			preCallback: _preCallback,
			servicesContext: _servicesContext,
			to: _to,
			...baseProps
		} = this.props;

		// NOTE: we only return the "to" prop
		// when there is no _childrenData
		return {
			to: !_childrenData ? _to : undefined,
			...baseProps,
		};
	}

	private generateInternalUrl(
		url: string,
		isMarketUrl?: boolean,
		noPrefix?: boolean,
	): string | null {
		if (!url && isMarketUrl) {
			return null;
		}

		if (noPrefix) {
			return url;
		}

		return this.baseUrl + url;
	}

	private generateUrl() {
		const {
			legacyAjaxRequest,
			marketAjaxUrl = '',
			servicesContext,
			target,
			to = '',
		} = this.props;

		const isExternalUrl = servicesContext.linkActions.isExternalUrl(to, target);

		const fullOddscheckerUrl =
			!isExternalUrl && to?.includes('oddschecker.com');

		const url = isExternalUrl
			? this.formatExternalUrl(to)
			: this.generateInternalUrl(to, false, fullOddscheckerUrl);

		let decodedUrl = (legacyAjaxRequest ? url : to) ?? '';

		try {
			decodedUrl = decodeURIComponent(decodedUrl);
		} catch (e) {
			console.error(e);
		}

		const fullMarketAjaxUrl = this.generateInternalUrl(
			marketAjaxUrl,
			true,
			false,
		);

		return {
			decodedUrl,
			isExternalUrl,
			fullMarketAjaxUrl,
			fullOddscheckerUrl,
			url,
		};
	}

	public render(): JSX.Element {
		const { children, id = '', legacyAjaxRequest, to = '' } = this.props;

		const baseProps = this.generateBaseProps();

		const { decodedUrl } = this.generateUrl();

		// Return span if insufficient payload data

		if (decodedUrl.endsWith('/us/undefined')) {
			return <span {...baseProps}>{children}</span>;
		}

		const linkId = id.replace(/ |-/g, '_');

		// legacy AJAX request
		if (legacyAjaxRequest) {
			return (
				<a
					{...baseProps}
					href={decodedUrl}
					id={linkId}
					onClick={e => {
						this.handleClick(e, decodedUrl);
					}}
				>
					{children}
				</a>
			);
		}

		// native achor behaviour
		return (
			<a {...baseProps} href={removeLeadingSlash(to)} id={linkId}>
				{children}
			</a>
		);
	}
}

export function withContext<T extends WithContext = WithContext>(
	Component: React.ComponentType<T>,
) {
	const WrappedComponent = (props: Omit<T, keyof WithContext>) => {
		const loaderContext = useLoaderContext();
		const modalContext = useModalContext();
		const servicesContext = useServicesContext();

		return (
			<Component
				{...(props as T)}
				loaderContext={loaderContext}
				modalContext={modalContext}
				servicesContext={servicesContext}
			/>
		);
	};

	const displayName = Component.displayName || Component.name || 'Link';
	WrappedComponent.displayName = `withContext(${displayName})`;

	return WrappedComponent;
}

const LinkWithContext = withContext(Link);

export default LinkWithContext;
